import React, { useState } from "react";
import ShieldIcon from "assets/shield.svg";
import { ToggleVisibility } from "components/common";
import { isValidEmail } from "@tomocredit/common-fe/utils/validation";
import { runPasswordValidator } from "utils/runPasswordValidator";
import { Link } from "react-router-dom";

export const MESSAGE_TEXT = {
  SERVER_ERROR: "Server error",
  EXISTING_ACCOUNT: "It looks like you already have an account with us. Please login to continue.",
  NO_EMAIL_OR_PASSWORD: "You need to enter an email and password",
  INVALID_EMAIL_OR_PASSWORD: "Invalid username and/or password",
  TOO_MANY_ATTEMPTS: "Too many failed attempts. Try again later.",
  EMAIL_INVALID: "Please enter a valid email address.",
  FIRST_NAME_INVALID: "Please enter a valid first name",
  LAST_NAME_INVALID: "Please enter a valid last name",
  CONFIRM_PASSWORD_NOT_MATCH: "Confirm password field does not match",
  NO_VALUE_ERROR: "This field is required.",
  PASSWORD_RESET_ACCOUNT_NOT_SETUP: "Your account has not yet been set up. Please check your email again for your invite link.",
  PASSWORD_RESET_PROCESS_SUCCESS: "Your request has been submitted! If the account exists, you will receive an email with password reset instructions.",
  PASSWORD_RESET_PROCESS_ERROR: "There is an error processing your request. Please try again later.",
};

const nameRegex = /^[A-Za-z ,.'-]+$/i;

const toInputClass = (readOnly, hasError) => {
  return `mt-1 w-full px-2 border border-neutral-400 rounded-sm bg-white outline-none ${readOnly ? "opacity-50" : ""} ${hasError ? "border border-error-600" : "border border-neutral-400"}`;
};

export const InputField = ({
  label,
  value,
  onChange = () => {},
  onBlur = () => {},
  type = "text",
  name,
  placeholder = "",
  className,
  loading,
  readOnly = false,
  error = "",
}) => {
  return (<div className={className}>
    {label && <div className="font-semibold">{label}</div>}
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      className={toInputClass(readOnly, error)}
      style={{ paddingTop: 7, paddingBottom: 7 }}
      disabled={loading}
    />
    <p className="text-xs font-medium text-error-600">{error}</p>
  </div>);
};

export const PasswordField = ({
  label,
  value,
  onChange = () => {},
  handleSubmit = () => {},
  onBlur = () => {},
  placeholder = "Enter your Password",
  hideToggle = false,
  className,
  loading,
  readOnly,
  error,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={className}>
      {label && <div className="font-semibold">{label}</div>}
      <div className="flex relative items-center">
        <input
          type={showPassword ? "text" : "password"}
          data-hj-allow
          name="password"
          placeholder={placeholder}
          value={value}
          className={toInputClass(readOnly, error)}
          style={{ paddingTop: 7, paddingBottom: 7 }}
          onChange={onChange}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
          }}
          onBlur={onBlur}
          disabled={loading}
        />
        {!hideToggle && <div className="absolute right-0 z-10 flex items-center mr-2" style={{ top: "50%", marginTop: 2 }}>
          <ToggleVisibility
            visible={!showPassword}
            onClick={toggleShowPassword}
            style={{
              height: 16,
              width: 16,
              transform: "translateY(-50%)",
            }}
          />
        </div>}
      </div>
      <p className="text-xs font-medium text-error-600">{error}</p>
    </div>
  );
};

export const Button = ({ children, loading, className, ...props }) => {
  const buttonClassName = `mx-auto text-center px-4 py-2 text-white rounded-md cursor-pointer focus:outline-none ${className} ${loading && "disabled opacity-50 cursor-not-allowed"}`;
  return (<button
    type="button"
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    className={buttonClassName}
    disabled={loading}
  >
    {children}
  </button>);
};

export const ErrorMessage = ({ message }) => (
  <div className={message ? "text-sm text-error-600 p-2 text-center" : "hidden"}>
    {message}
  </div>
);

export const SuccessMessage = ({ message }) => (
  <div className={message ? "text-sm p-2 text-center" : "hidden"}>
    {message}
  </div>
);

const LoginPageButton = ({
  buttonText = "Login",
  loading,
  onClick = () => {},
}) => {
  return (
    <div className="mt-6 flex flex-col justify-center">
      <Button
        loading={loading}
        onClick={onClick}
        className="w-5/6 bg-black hover:bg-neutral-600 focus:bg-neutral-700"
      >
        {buttonText}
      </Button>
      <div className="mt-2 flex justify-center items-center text-neutral-600 text-xs font-medium">
        <img src={ShieldIcon} alt="" className="inline mr-2" />
        <span>Protected with Bank Level Security.</span>
      </div>
    </div>
  );
};

export const LoginForm = ({
  email,
  setEmail = () => {},
  handleSubmit = () => {},
  loading,
  errorMessage,
  setErrorMessage = () => {},
}) => {
  const [password, setPassword] = useState("");

  const handleEmailInput = (e) => {
    setErrorMessage("");
    setEmail(e.target.value);
  };
  const handlePasswordInput = (e) => {
    setErrorMessage("");
    setPassword(e.target.value);
  };
  const handleLogin = () => handleSubmit({ email, password });

  return (
    <form className="mx-5 md:mx-0 flex flex-col justify-around h-full">
      <InputField
        label="Email"
        name="email"
        value={email}
        onChange={handleEmailInput}
        placeholder="Enter your email"
        className="mt-6 data-hj-allow"
      />
      <div className="relative">
        <PasswordField
          label="Password"
          type="password"
          value={password}
          onChange={handlePasswordInput}
          handleSubmit={handleLogin}
          placeholder="Enter your password"
          className="mt-6"
        />
        <div className="absolute right-0 top-0 mt-6">
          <Link
            to={{
              pathname: "/passwordrecovery",
              state: { email },
            }}
            className="underline hover:no-underline text-black text-sm"
          >
            Forgot Password
          </Link>
        </div>
      </div>
      <LoginPageButton
        buttonText="Login"
        loading={loading}
        onClick={handleLogin}
      />
      <ErrorMessage message={errorMessage} />
      <div className="w-full text-center mt-4">
        <span>Don&apos;t have an account? </span>
        <Link
          className="underline hover:no-underline focus:outline-none"
          to={{
            pathname: "/signup",
            state: { email },
          }}
        >
          Sign up
        </Link>
      </div>
    </form>
  );
};

export const SignUpForm = ({
  email,
  setEmail = () => {},
  handleSubmit = () => {},
  loading,
  errorMessage,
  setErrorMessage = () => {},
  showExistingAccount,
  setShowExistingAccount = () => {},
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [emailError, setEmailError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const validateEmail = (emailInput) => {
    setErrorMessage("");
    setEmailError("");
    if (!emailInput) {
      setEmailError(MESSAGE_TEXT.NO_VALUE_ERROR);
      return false;
    }
    if (!isValidEmail(emailInput)) {
      setEmailError(MESSAGE_TEXT.EMAIL_INVALID);
      return false;
    }
    return true;
  };
  const validateFirstName = (nameInput) => {
    setErrorMessage("");
    setFirstNameError("");
    if (!nameInput) {
      setFirstNameError(MESSAGE_TEXT.NO_VALUE_ERROR);
      return false;
    }
    if (!nameRegex.test(nameInput)) {
      setFirstNameError(MESSAGE_TEXT.FIRST_NAME_INVALID);
      return false;
    }
    return true;
  };
  const validateLastName = (nameInput) => {
    setErrorMessage("");
    setLastNameError("");
    if (!nameInput) {
      setLastNameError(MESSAGE_TEXT.NO_VALUE_ERROR);
      return false;
    }
    if (!nameRegex.test(nameInput)) {
      setLastNameError(MESSAGE_TEXT.LAST_NAME_INVALID);
      return false;
    }
    return true;
  };
  const validatePassword = (passwordInput) => {
    setErrorMessage("");
    setPasswordError("");
    if (!passwordInput) {
      setPasswordError(MESSAGE_TEXT.NO_VALUE_ERROR);
      return false;
    }
    const [passwordValidated, message] = runPasswordValidator(passwordInput);
    if (!passwordValidated) {
      setPasswordError(message);
      return false;
    }
    return true;
  };
  const validateConfirmPassword = (confirmPasswordInput) => {
    setErrorMessage("");
    setConfirmPasswordError("");
    if (!confirmPasswordInput) {
      setConfirmPasswordError(MESSAGE_TEXT.NO_VALUE_ERROR);
      return false;
    }
    if (password !== confirmPasswordInput) {
      setConfirmPasswordError(MESSAGE_TEXT.CONFIRM_PASSWORD_NOT_MATCH);
      return false;
    }
    return true;
  };

  const presubmitCheck = () => {
    const check = [];
    check.push(validateEmail(email));
    check.push(validateFirstName(firstName));
    check.push(validateLastName(lastName));
    check.push(validatePassword(password));
    check.push(validateConfirmPassword(confirmPassword));
    return check.every((ck) => ck === true);
  };

  const handleSignUp = () => {
    if (presubmitCheck()) {
      handleSubmit({ email, password, firstName, lastName });
    }
  };

  return (
    <form className="mx-5 md:mx-0 flex flex-col justify-around h-full text-black">
      {showExistingAccount && (
        <div className="text-error-600 text-center">
          {MESSAGE_TEXT.EXISTING_ACCOUNT}
        </div>
      )}
      <InputField
        label="Email"
        name="email"
        value={email}
        onChange={(e) => {
          const emailInput = e.target.value;
          setEmail(emailInput);
          validateEmail(emailInput);
          setShowExistingAccount(false);
        }}
        onBlur={() => validateEmail(email)}
        error={emailError}
        placeholder="Enter your email"
        className=""
        loading={loading}
      />
      <div className="flex space-x-6 mt-4">
        <InputField
          label="Legal first name"
          name="firstName"
          value={firstName}
          onChange={(e) => {
            const firstNameInput = e.target.value;
            setFirstName(firstNameInput);
            validateFirstName(firstNameInput);
          }}
          onBlur={() => validateFirstName(firstName)}
          error={firstNameError}
          placeholder="First Name"
          className="flex-1"
          loading={loading}
        />
        <InputField
          label="Legal last name"
          name="lastName"
          value={lastName}
          onChange={(e) => {
            const lastNameInput = e.target.value;
            setLastName(lastNameInput);
            validateLastName(lastNameInput);
          }}
          onBlur={() => validateLastName(lastName)}
          error={lastNameError}
          placeholder="Last Name"
          className="flex-1"
          loading={loading}
        />
      </div>
      <PasswordField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => {
          const passwordInput = e.target.value;
          setPassword(passwordInput);
          validatePassword(passwordInput);
        }}
        onBlur={() => validatePassword(password)}
        error={passwordError}
        placeholder="Set your password"
        className="mt-4"
        loading={loading}
      />
      <PasswordField
        label=""
        type="password"
        value={confirmPassword}
        onChange={(e) => {
          const confirmPasswordInput = e.target.value;
          setConfirmPassword(confirmPasswordInput);
          validateConfirmPassword(confirmPasswordInput);
        }}
        onBlur={() => validateConfirmPassword(confirmPassword)}
        error={confirmPasswordError}
        placeholder="Confirm your password"
        className="mt-1"
        hideToggle
        loading={loading}
      />
      <div className="mt-1 font-medium text-xs text-neutral-600">Must contain at least 8 characters and 1 letter and 1 symbol.</div>
      <LoginPageButton
        buttonText="Get started"
        loading={loading}
        onClick={handleSignUp}
      />
      <ErrorMessage message={errorMessage} />
      <div className="w-full text-center mt-4">
        <span>Already have an account? </span>
        <Link
          className="underline hover:no-underline focus:outline-none"
          to={{
            pathname: "/login",
            state: { email },
          }}
        >
          Log in
        </Link>
      </div>
    </form>
  );
};

export const ResetForm = ({
  email,
  setEmail = () => {},
  handleSubmit = () => {},
  loading,
  errorMessage,
  successMessage,
}) => {

  return (
    <form className="mx-5 md:mx-0 flex flex-col justify-around h-full">
      <InputField
        label="Email"
        name="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        className="mt-6 data-hj-allow"
      />
      <LoginPageButton
        buttonText="Submit"
        loading={loading}
        onClick={() => handleSubmit()}
      />
      <ErrorMessage message={errorMessage} />
      <SuccessMessage message={successMessage} />
      <div className="w-full text-center mt-4">
        <Link
          to={{
            pathname: "/login",
            state: { email },
          }}
          className="underline"
        >
          Return to log in
        </Link>
      </div>
    </form>
  );
};